import { useState } from 'react'
import { api } from '../lib/api'

// SheetJS is large and only needed here — load it on demand so it stays out of
// the main bundle.
async function loadXLSX() {
  return import('xlsx')
}

const TARGETS: { key: string; label: string }[] = [
  { key: 'ignore', label: '— Ignore —' },
  { key: 'technician_name', label: 'Technician name' },
  { key: 'customer_name', label: 'Customer name' },
  { key: 'customer_phone', label: 'Customer phone' },
  { key: 'address', label: 'Address' },
  { key: 'description', label: 'Description / issue' },
  { key: 'incident_date', label: 'Incident / install date' },
  { key: 'severity', label: 'Severity' },
  { key: 'notes', label: 'Notes (→ first comment)' },
  { key: 'scheduled_repair_date', label: 'Scheduled repair date' },
  { key: 'completion_date', label: 'Completion date (→ closes case)' },
]

function guess(header: string): string {
  const n = header.toLowerCase().replace(/[^a-z]/g, '')
  if (n.includes('technician')) return 'technician_name'
  if (n.includes('phone')) return 'customer_phone'
  if (n.includes('customer') || n === 'name') return 'customer_name'
  if (n.includes('address')) return 'address'
  if (n.includes('issue') || n.includes('description') || n.includes('damage')) return 'description'
  if (n.includes('install') || n.includes('incident')) return 'incident_date'
  if (n.includes('severity')) return 'severity'
  if (n.includes('note')) return 'notes'
  if (n.includes('scheduled')) return 'scheduled_repair_date'
  if (n.includes('completion') || n.includes('complete')) return 'completion_date'
  return 'ignore'
}

function pad(n: number) {
  return String(n).padStart(2, '0')
}
function cell(v: unknown): string {
  if (v instanceof Date) return `${v.getFullYear()}-${pad(v.getMonth() + 1)}-${pad(v.getDate())}`
  if (v == null) return ''
  return String(v).trim()
}

type Parsed = { headers: string[]; rows: Record<string, unknown>[] }

export function ImportWizard() {
  const [parsed, setParsed] = useState<Parsed | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onFile(file: File) {
    setError(null)
    setResult(null)
    try {
      const XLSX = await loadXLSX()
      const buf = await file.arrayBuffer()
      const wb = XLSX.read(buf, { type: 'array', cellDates: true })
      const ws = wb.Sheets[wb.SheetNames[0]]
      const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' })
      const headerRow = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1 })[0] ?? []
      const headers = (headerRow as unknown[]).map((h) => String(h)).filter(Boolean)
      if (headers.length === 0 || rows.length === 0) {
        setError('That sheet looks empty.')
        return
      }
      setParsed({ headers, rows })
      setMapping(Object.fromEntries(headers.map((h) => [h, guess(h)])))
    } catch {
      setError('Could not read that file. Please upload a .xlsx or .csv.')
    }
  }

  function buildRows(): Record<string, string>[] {
    if (!parsed) return []
    return parsed.rows.map((r) => {
      const o: Record<string, string> = {}
      for (const h of parsed.headers) {
        const t = mapping[h]
        if (t && t !== 'ignore') o[t] = cell(r[h])
      }
      return o
    })
  }

  async function doImport() {
    const rows = buildRows()
    setBusy(true)
    setError(null)
    try {
      const res = await api.importCases(rows)
      setResult(res.created)
      setParsed(null)
    } catch {
      setError('Import failed. Check the mapping and try again.')
    } finally {
      setBusy(false)
    }
  }

  const mappedTargets = parsed
    ? [...new Set(Object.values(mapping).filter((t) => t !== 'ignore'))]
    : []
  const preview = parsed ? buildRows().slice(0, 5) : []

  return (
    <div>
      {result != null && (
        <p className="form-ok">Imported {result} case{result === 1 ? '' : 's'}. They're now in your case lists.</p>
      )}

      {!parsed ? (
        <div>
          <p style={{ color: 'var(--gray)', fontSize: 14, marginBottom: 10 }}>
            Upload your existing damage log (.xlsx or .csv). You'll preview and adjust the column
            mapping before anything is imported.
          </p>
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={(e) => e.target.files?.[0] && void onFile(e.target.files[0])}
          />
        </div>
      ) : (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>
            {parsed.rows.length} rows · map each column:
          </div>
          <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
            {parsed.headers.map((h) => (
              <div key={h} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, alignItems: 'center' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{h}</div>
                <select
                  value={mapping[h] ?? 'ignore'}
                  onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                  style={controlStyle}
                >
                  {TARGETS.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div style={{ fontWeight: 600, marginBottom: 8 }}>Preview (first 5 rows)</div>
          <div style={{ overflowX: 'auto', marginBottom: 16 }}>
            <table className="table">
              <thead>
                <tr>{mappedTargets.map((t) => <th key={t}>{TARGETS.find((x) => x.key === t)?.label ?? t}</th>)}</tr>
              </thead>
              <tbody>
                {preview.map((row, i) => (
                  <tr key={i}>
                    {mappedTargets.map((t) => (
                      <td key={t} style={{ fontSize: 13 }}>{row[t] || '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 12 }}>
            Rows with a Completion date import as <strong>Closed</strong>; a Scheduled repair date
            (no completion) as <strong>In Progress</strong>; everything else as <strong>Open</strong>.
          </p>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" onClick={() => void doImport()} disabled={busy}>
              {busy ? 'Importing…' : `Import ${parsed.rows.length} rows`}
            </button>
            <button className="btn" style={{ border: '1px solid var(--line)' }} onClick={() => setParsed(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && <p className="form-error">{error}</p>}
    </div>
  )
}

const controlStyle: React.CSSProperties = {
  padding: '8px 10px',
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  font: 'inherit',
  background: 'var(--bg)',
}
